import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bell,
  Calendar as CalendarIcon,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Plus,
  RefreshCw,
  Sunrise,
  Trash2,
  X,
} from 'lucide-react'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import {
  CalendarData,
  CalendarDay,
  EventItem,
  RecurrenceType,
  ReminderItem,
  TaskItem,
  calendarApi,
  api,
} from '../api/client'
import { RatingPrompt } from '../components/RatingPrompt'
import { DatePicker } from '../components/ui/DatePicker'
import { ErrorCard } from '../components/ui/ErrorCard'
import { TimePicker } from '../components/ui/TimePicker'
import { Skeleton } from '../components/ui/Skeleton'
import { WelcomeSheet } from '../components/WelcomeSheet'
import { useTelegram } from '../hooks/useTelegram'

// ── Константы ─────────────────────────────────────────────────────────────────

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

const DOT_COLORS = {
  reminder: '#4A6CF7',
  task:     '#34C759',
  event:    '#AF52DE',
} as const

const RECURRENCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '',         label: 'Никогда' },
  { value: 'daily',    label: 'Ежедневно' },
  { value: 'weekdays', label: 'По будням' },
  { value: 'weekly',   label: 'Еженедельно' },
  { value: 'monthly',  label: 'Ежемесячно' },
]

const PRIORITIES: Array<{ value: Priority; label: string }> = [
  { value: 'low',    label: 'Низкий' },
  { value: 'normal', label: 'Обычный' },
  { value: 'high',   label: 'Высокий' },
]

type Priority = 'low' | 'normal' | 'high'
type ItemKind = 'reminder' | 'task' | 'event'

/** Листы открываются стопкой: активен только верхний, закрытие возвращает предыдущий. */
type SheetName = 'day' | 'event' | 'form' | 'undated'

// ── Работа с датами ───────────────────────────────────────────────────────────

/** Локальная дата → 'YYYY-MM-DD' (без сдвига через UTC). */
function ymd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/**
 * Время из ISO-строки берём срезом, а не через Date: бэкенд уже перевёл
 * его в часовой пояс пользователя, а Date отрендерил бы в поясе устройства.
 */
function isoTime(iso: string): string {
  return iso.slice(11, 16)
}

function isoDate(iso: string): string {
  return iso.slice(0, 10)
}

/** 42 ячейки сетки: понедельник до 1-го числа и далее подряд. */
function buildGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1)
  const mondayOffset = (first.getDay() + 6) % 7
  return Array.from({ length: 42 }, (_, i) =>
    new Date(year, month, 1 - mondayOffset + i),
  )
}

function formatDayTitle(key: string): string {
  const [y, m, d] = key.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  const weekday = date.toLocaleDateString('ru', { weekday: 'long' })
  const month = date.toLocaleDateString('ru', { month: 'long' })
  return `${d} ${month}, ${weekday}`
}

function emptyDay(): CalendarDay {
  return { reminders: [], tasks: [], events: [] }
}

/** Русский плюрал для слова «событие». */
function pluralEvents(n: number): string {
  const n10 = n % 10
  const n100 = n % 100
  if (n10 === 1 && n100 !== 11) return 'событие'
  if (n10 >= 2 && n10 <= 4 && (n100 < 10 || n100 >= 20)) return 'события'
  return 'событий'
}


/** Текущее локальное время как 'HH:MM' — сопоставимо с isoTime(). */
function nowHHMM(): string {
  const d = new Date()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** Строка повестки дня. `past` — время уже прошло (считается только для сегодня). */
type SummaryLine = { time: string; past: boolean } & (
  | { kind: 'event'; event: CalendarDay['events'][number] }
  | { kind: 'reminder'; reminder: CalendarDay['reminders'][number] }
  | { kind: 'task'; task: CalendarDay['tasks'][number] }
)

/**
 * Полная повестка дня: все события, напоминания и задачи с дедлайном — одним
 * списком по времени. Прошедшее не скрываем, а помечаем `past` и приглушаем
 * в рендере, иначе к вечеру панель пустела бы и день был не виден целиком.
 */
function buildSummary(day: CalendarDay, dayKey: string, isToday: boolean): SummaryLine[] {
  const now = isToday ? nowHHMM() : ''
  const isPast = (time: string) => isToday && time < now

  const lines: SummaryLine[] = [
    ...day.events.map((event): SummaryLine => {
      const time = isoTime(event.starts_at)
      return { kind: 'event', time, past: isPast(time), event }
    }),
    ...day.reminders.map((reminder): SummaryLine => {
      const time = isoTime(reminder.remind_at)
      return { kind: 'reminder', time, past: isPast(time), reminder }
    }),
    // Задачи без дедлайна и выполненные в повестку дня не идут — они живут
    // в счётчиках и в листе дня.
    ...day.tasks
      .filter(t => t.due_at && isoDate(t.due_at) === dayKey && t.status !== 'done')
      .map((task): SummaryLine => {
        const time = isoTime(task.due_at!)
        return { kind: 'task', time, past: isPast(time), task }
      }),
  ]

  return lines.sort((a, b) => a.time.localeCompare(b.time))
}

// ── Bottom sheet ──────────────────────────────────────────────────────────────

interface SheetProps {
  onClose: () => void
  children: ReactNode
  zIndex?: number
}

/**
 * Отслеживает видимую область (visualViewport). Когда открыта экранная
 * клавиатура, она перекрывает низ layout-вьюпорта, но не уменьшает его высоту —
 * поэтому лист, прижатый к низу, уезжал бы под клавиатуру. Возвращаем реальную
 * высоту видимой области и высоту клавиатуры, чтобы поднять лист над ней.
 */
function useViewport() {
  const [vp, setVp] = useState(() => ({
    height: window.visualViewport?.height ?? window.innerHeight,
    keyboard: 0,
  }))

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const update = () => {
      // Часть layout-вьюпорта, скрытая клавиатурой (снизу).
      const keyboard = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      setVp({ height: vv.height, keyboard })
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])

  return vp
}

// Сколько лист уезжает вниз при закрытии. Столько же живёт таймер до
// реального размонтирования — раньше его не было вовсе, и лист исчезал рывком.
const SHEET_EXIT_MS = 200

function Sheet({ onClose, children, zIndex = 50 }: SheetProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  // active: жест уже опознан как свайп закрытия (вертикальный, от верха контента).
  const drag = useRef<{ x: number; y: number; active: boolean } | null>(null)
  const [dragY, setDragY] = useState(0)
  const [closing, setClosing] = useState(false)
  const vp = useViewport()

  // Закрытие в две фазы: сначала уезжаем, потом сообщаем наверх. Родитель
  // размонтирует лист, поэтому отдать ему управление сразу — значит не успеть
  // ничего показать. onClose держим в ref: смена его идентичности при
  // перерисовке не должна перезапускать таймер уже идущего закрытия.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!closing) return
    const timer = setTimeout(() => onCloseRef.current(), SHEET_EXIT_MS)
    return () => clearTimeout(timer)
  }, [closing])

  const startClose = () => setClosing(true)

  // Блокируем скролл страницы под шитом
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  // iOS-фолбэк к visualViewport: он обновляется с задержкой и не всегда поднимает
  // поле над клавиатурой. При фокусе доскролливаем поле в центр видимой области,
  // дав клавиатуре ~300 мс на открытие. Работает по своей оси (скролл контента
  // внутри листа) и не конфликтует с подъёмом всего листа через paddingBottom.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null
      if (!el || !el.matches('input, textarea, select')) return
      clearTimeout(timer)
      timer = setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 300)
    }
    root.addEventListener('focusin', onFocusIn)
    return () => {
      clearTimeout(timer)
      root.removeEventListener('focusin', onFocusIn)
    }
  }, [])

  const scrollTop = () => {
    const el = rootRef.current?.querySelector<HTMLElement>('[data-sheet-scroll]')
    return el?.scrollTop ?? 0
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    drag.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, active: false }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    const d = drag.current
    if (!d) return
    const dx = e.touches[0].clientX - d.x
    const dy = e.touches[0].clientY - d.y
    if (!d.active) {
      // Горизонтальное движение — это не свайп закрытия, отменяем жест.
      if (Math.abs(dx) > Math.abs(dy)) { drag.current = null; return }
      // Тянут вверх или контент проскроллен не в самый верх — отдаём нативному скроллу.
      if (dy <= 0 || scrollTop() > 0) return
      // Вертикально вниз от верха контента — с этого момента тащим лист.
      d.active = true
    }
    setDragY(Math.max(0, dy))
  }

  const handleTouchEnd = () => {
    if (dragY > 80) {
      // Дотащили — дальше лист доезжает сам, с того места, где отпустили:
      // transition ретаргетится от текущего значения, рывка нет.
      startClose()
      drag.current = null
      return
    }
    drag.current = null
    setDragY(0)
  }

  // Через портал в body: лист не попадает в stacking context страницы (её
  // page-enter оставляет transform, который иначе перекрывает порядок наложения
  // и роняет сетку поверх формы на iOS). Так лист гарантированно выше сетки.
  return createPortal(
    <div
      className="fixed inset-0 flex flex-col justify-end"
      // paddingBottom поднимает лист над клавиатурой, когда она открыта.
      // translateZ(0) промоутит оверлей в собственный GPU-слой — на iOS Safari
      // это гарантирует, что fixed-лист композитится поверх backdrop-filter-слоёв.
      style={{ zIndex, paddingBottom: vp.keyboard, transform: 'translateZ(0)' }}
    >
      <div
        className="absolute inset-0 bg-black/60 scrim-enter"
        style={{
          opacity: closing ? 0 : undefined,
          transition: `opacity ${SHEET_EXIT_MS}ms var(--ease-out)`,
        }}
        onClick={startClose}
      />
      <div
        ref={rootRef}
        className="relative bg-surface-1 rounded-t-[20px] flex flex-col sheet-enter overflow-hidden"
        style={{
          // Не выше 90% видимой области; при открытой клавиатуре это высота
          // над ней, поэтому лист всегда помещается и скроллится внутри.
          maxHeight: Math.round(vp.height * 0.9),
          // Три состояния: уезжаем вниз, тащим пальцем, стоим на месте.
          // Во время перетаскивания transition выключен — лист обязан идти
          // за пальцем кадр в кадр. В остальных случаях он включён, поэтому
          // и отпущенный на полпути лист возвращается плавно, и закрытие
          // подхватывается с той точки, где палец отпустили.
          transform: closing
            ? 'translateY(100%)'
            : dragY ? `translateY(${dragY}px)` : undefined,
          transition: !closing && dragY
            ? 'none'
            : `transform ${SHEET_EXIT_MS}ms var(--ease-out)`,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="shrink-0">
          <div className="sheet-handle" />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}

// ── Сетка месяца ──────────────────────────────────────────────────────────────

interface GridProps {
  year: number
  month: number
  days: Record<string, CalendarDay>
  selected: string | null
  onSelect: (key: string) => void
  onSwipe: (direction: -1 | 1) => void
}

function MonthGrid({ year, month, days, selected, onSelect, onSwipe }: GridProps) {
  const cells = useMemo(() => buildGrid(year, month), [year, month])
  const todayKey = ymd(new Date())
  const touch = useRef<{ x: number; y: number } | null>(null)

  const handleTouchStart = (e: React.TouchEvent) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touch.current) return
    const dx = e.changedTouches[0].clientX - touch.current.x
    const dy = e.changedTouches[0].clientY - touch.current.y
    // Горизонтальный свайп — только если он заметно длиннее вертикального
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      onSwipe(dx < 0 ? 1 : -1)
    }
    touch.current = null
  }

  return (
    <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map(w => (
          <div key={w} className="text-center text-ink-2" style={{ fontSize: 11 }}>
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map(date => {
          const key = ymd(date)
          const outside = date.getMonth() !== month
          const isToday = key === todayKey
          const day = days[key]

          // Точки-индикаторы показываем только для дней текущего месяца
          const dots: string[] = []
          if (!outside) {
            if (day?.reminders.length) dots.push(DOT_COLORS.reminder)
            if (day?.tasks.length)     dots.push(DOT_COLORS.task)
            if (day?.events.length)    dots.push(DOT_COLORS.event)
          }

          return (
            <button
              key={key}
              onClick={() => onSelect(key)}
              className={`cal-cell ${outside ? 'outside' : ''} ${selected === key ? 'selected' : ''}`}
            >
              <span
                className={isToday ? 'cal-today-circle' : ''}
                style={{ fontSize: 15, color: isToday ? '#fff' : 'var(--color-text-0)' }}
              >
                {date.getDate()}
              </span>
              <span className="cal-dots">
                {dots.slice(0, 3).map(color => (
                  <span key={color} className="cal-dot" style={{ background: color }} />
                ))}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Сводка дня ────────────────────────────────────────────────────────────────

interface SummaryProps {
  label: string
  lines: SummaryLine[]
  onOpenEvent: (e: CalendarDay['events'][number]) => void
  onEditReminder: (r: CalendarDay['reminders'][number]) => void
  onEditTask: (t: CalendarDay['tasks'][number]) => void
  onAddToday: () => void
}

function TodaySummary({
  label, lines, onOpenEvent, onEditReminder, onEditTask, onAddToday,
}: SummaryProps) {
  return (
    <div className="bg-surface-1 border border-line-1 rounded-xl p-3">
      <p className="section-label">{label}</p>

      {lines.length === 0 ? (
        <div className="flex items-center justify-between gap-3 px-1">
          <span className="text-sm text-ink-2">На сегодня ничего не запланировано</span>
          <button onClick={onAddToday} className="btn-active text-sm text-accent shrink-0">
            Добавить
          </button>
        </div>
      ) : (
        <div className="space-y-0.5">
          {lines.map(line => {
            // Прошедшее приглушаем, но оставляем кликабельным.
            const dim = line.past ? 'opacity-45' : ''
            const row = `btn-active w-full flex items-center gap-2.5 text-left py-1 ${dim}`

            if (line.kind === 'event') {
              return (
                <button
                  key={`ev-${line.event.id}`}
                  onClick={() => onOpenEvent(line.event)}
                  className={row}
                >
                  <CalendarIcon size={15} style={{ color: DOT_COLORS.event }} className="shrink-0" />
                  <span className="text-sm text-ink-1 tabular-nums shrink-0">{line.time}</span>
                  <span className="text-sm text-ink flex-1 min-w-0 truncate">{line.event.title}</span>
                  {line.event.location && (
                    <span className="flex items-center gap-0.5 text-xs text-ink-2 shrink-0 max-w-[35%] truncate">
                      <MapPin size={11} className="shrink-0" />
                      {line.event.location}
                    </span>
                  )}
                </button>
              )
            }
            if (line.kind === 'reminder') {
              return (
                <button
                  key={`rm-${line.reminder.id}`}
                  onClick={() => onEditReminder(line.reminder)}
                  className={row}
                >
                  <Bell size={15} style={{ color: DOT_COLORS.reminder }} className="shrink-0" />
                  <span className="text-sm text-ink-1 tabular-nums shrink-0">{line.time}</span>
                  <span className="text-sm text-ink flex-1 min-w-0 truncate">{line.reminder.text}</span>
                </button>
              )
            }
            return (
              <button
                key={`tk-${line.task.id}`}
                onClick={() => onEditTask(line.task)}
                className={row}
              >
                <CheckSquare size={15} style={{ color: DOT_COLORS.task }} className="shrink-0" />
                <span className="text-sm text-ink-1 tabular-nums shrink-0">{line.time}</span>
                <span className="text-sm text-ink flex-1 min-w-0 truncate">{line.task.title}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Форма создания / редактирования ───────────────────────────────────────────

interface EditorState {
  kind: ItemKind
  id?: number
  title: string
  date: string
  time: string
  recurrence: string
  /** true — напоминание с триггером «когда проснусь» (trigger_type='on_wake') */
  wake: boolean
  priority: Priority
  hasDue: boolean
  dueTime: string
  tags: string
  allDay: boolean
  endTime: string
  location: string
  description: string
}

function newEditor(kind: ItemKind, date: string): EditorState {
  return {
    kind,
    title: '',
    date,
    time: '09:00',
    recurrence: '',
    wake: false,
    priority: 'normal',
    hasDue: false,
    dueTime: '18:00',
    tags: '',
    allDay: false,
    endTime: '10:00',
    location: '',
    description: '',
  }
}

interface EditorProps {
  state: EditorState
  onChange: (s: EditorState) => void
  onClose: () => void
  onSubmit: () => void
  onDelete: () => void
  busy: boolean
}

/** Переключатель в стиле проекта (как в «Уведомлениях»). */
function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      role="switch"
      aria-checked={checked}
      className="relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent focus:outline-none"
      style={{
        backgroundColor: checked ? 'var(--color-accent)' : 'var(--color-bg-3)',
        transition: 'background-color 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}
    >
      <span
        className="inline-block h-5 w-5 rounded-full bg-white shadow"
        style={{
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      />
    </button>
  )
}

/** Метка над полем — единый вертикальный ритм для всех полей формы. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="form-label">{label}</label>
      {children}
    </div>
  )
}

/** Строка «подпись + переключатель», выровненная по центру. */
function ToggleRow({ label, checked, onChange }: {
  label: string; checked: boolean; onChange: () => void
}) {
  return (
    <div className="flex items-center justify-between gap-3 min-h-[24px]">
      <span className="text-base text-ink">{label}</span>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  )
}

// Форма — отдельная полноэкранная страница, а не всплывающий лист. На iOS
// composite-слои календарной сетки (backdrop-filter шапки и т.п.) непредсказуемо
// всплывали поверх fixed-листа из портала. Как страница форма рендерится вместо
// календаря — сетка не в DOM, перекрывать нечем. Заголовок здесь на сплошном
// фоне, без backdrop-filter, чтобы вложенные пикеры тоже ничем не перекрывались.
function EditorPage({ state, onChange, onClose, onSubmit, onDelete, busy }: EditorProps) {
  const { haptic } = useTelegram()
  const isEdit = state.id !== undefined
  const set = (patch: Partial<EditorState>) => onChange({ ...state, ...patch })

  // У события конец должен быть позже начала (кроме «весь день»). Сравнение
  // строк 'HH:MM' корректно в пределах одного дня. Без этого можно было создать
  // событие с отрицательной длительностью, которое ломало синхронизацию с
  // Google/Apple Calendar (там ends_at уходил раньше starts_at).
  const timeRangeInvalid =
    state.kind === 'event' && !state.allDay && state.endTime <= state.time
  const canSubmit = state.title.trim().length > 0 && !busy && !timeRangeInvalid

  return (
    <div className="flex flex-col min-h-full page-enter">
      {/* Заголовок — sticky, сплошной фон (без backdrop-filter) */}
      <div className="sticky top-0 z-10 bg-bg-primary flex items-center justify-between px-4 pt-5 pb-3">
        <p className="text-h3 text-ink">{isEdit ? 'Изменить' : 'Новая запись'}</p>
        <button onClick={onClose} className="btn-active text-ink-2 p-1" aria-label="Закрыть">
          <X size={22} />
        </button>
      </div>

      <div className="flex-1 px-4 py-4 space-y-4">
        {/* Тип — только при создании */}
        {!isEdit && (
          <div className="segmented-control">
            {(['reminder', 'task', 'event'] as ItemKind[]).map(k => (
              <button
                key={k}
                onClick={() => { haptic.selectionChanged(); set({ kind: k }) }}
                className={`segment btn-active ${state.kind === k ? 'active' : ''}`}
              >
                {k === 'reminder' ? 'Напоминание' : k === 'task' ? 'Задача' : 'Событие'}
              </button>
            ))}
          </div>
        )}

        <Field label="Название">
          <input
            className="form-input"
            value={state.title}
            autoFocus={!isEdit}
            placeholder={
              state.kind === 'reminder' ? 'О чём напомнить'
                : state.kind === 'task' ? 'Что нужно сделать'
                : 'Название события'
            }
            onChange={e => set({ title: e.target.value })}
          />
        </Field>

        {/* Дата — общая для события и напоминания (кроме «когда проснусь»).
            У задачи дата живёт внутри блока дедлайна: показывать её без
            включённого дедлайна нельзя — она бы молча отбрасывалась. */}
        {state.kind !== 'task' && !(state.kind === 'reminder' && state.wake) && (
          <Field label="Дата">
            <DatePicker value={state.date} onChange={date => set({ date })} />
          </Field>
        )}

        {/* ── Напоминание ── */}
        {state.kind === 'reminder' && (
          <>
            {/* Переключатель типа триггера — только при создании */}
            {!isEdit && (
              <div className="segmented-control">
                <button
                  onClick={() => { haptic.selectionChanged(); set({ wake: false }) }}
                  className={`segment btn-active ${!state.wake ? 'active' : ''}`}
                >
                  По времени
                </button>
                <button
                  onClick={() => { haptic.selectionChanged(); set({ wake: true }) }}
                  className={`segment btn-active ${state.wake ? 'active' : ''}`}
                >
                  Когда проснусь
                </button>
              </div>
            )}
            {!state.wake && (
              <>
                <Field label="Время">
                  <TimePicker value={state.time} onChange={time => set({ time })} />
                </Field>
                <Field label="Повтор">
                  <select
                    className="form-input"
                    value={state.recurrence}
                    onChange={e => set({ recurrence: e.target.value })}
                  >
                    {RECURRENCE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>
              </>
            )}
            {state.wake && (
              <p className="text-xs text-ink-2">
                Напомню при первой активности после того, как проснёшься.
              </p>
            )}
          </>
        )}

        {/* ── Задача ── */}
        {state.kind === 'task' && (
          <>
            <Field label="Приоритет">
              <div className="segmented-control">
                {PRIORITIES.map(p => (
                  <button
                    key={p.value}
                    onClick={() => { haptic.selectionChanged(); set({ priority: p.value }) }}
                    className={`segment btn-active ${state.priority === p.value ? 'active' : ''}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </Field>
            <ToggleRow
              label="Указать дедлайн"
              checked={state.hasDue}
              onChange={() => { haptic.selectionChanged(); set({ hasDue: !state.hasDue }) }}
            />
            {state.hasDue && (
              <>
                <Field label="Дата дедлайна">
                  <DatePicker value={state.date} onChange={date => set({ date })} />
                </Field>
                <Field label="Время дедлайна">
                  <TimePicker value={state.dueTime} onChange={dueTime => set({ dueTime })} />
                </Field>
              </>
            )}
            <Field label="Теги (через запятую)">
              <input
                className="form-input"
                value={state.tags}
                placeholder="работа, встреча"
                onChange={e => set({ tags: e.target.value })}
              />
            </Field>
          </>
        )}

        {/* ── Событие ── */}
        {state.kind === 'event' && (
          <>
            <ToggleRow
              label="Весь день"
              checked={state.allDay}
              onChange={() => { haptic.selectionChanged(); set({ allDay: !state.allDay }) }}
            />
            {!state.allDay && (
              <div className="grid grid-cols-2 gap-3">
                {/* min-w-0: иначе колонки не сжимаются под ширину time-поля */}
                <div className="min-w-0">
                  <label className="form-label">Начало</label>
                  <TimePicker value={state.time} onChange={time => set({ time })} />
                </div>
                <div className="min-w-0">
                  <label className="form-label">Конец</label>
                  <TimePicker value={state.endTime} onChange={endTime => set({ endTime })} />
                </div>
              </div>
            )}
            {timeRangeInvalid && (
              <p className="text-xs" style={{ color: 'var(--color-danger)' }}>
                Конец должен быть позже начала
              </p>
            )}
            <Field label="Место">
              <input
                className="form-input"
                value={state.location}
                placeholder="Необязательно"
                onChange={e => set({ location: e.target.value })}
              />
            </Field>
            <Field label="Описание">
              <textarea
                className="form-input"
                rows={3}
                value={state.description}
                placeholder="Необязательно"
                onChange={e => set({ description: e.target.value })}
                style={{ resize: 'none' }}
              />
            </Field>
          </>
        )}
      </div>

      <div
        className="flex gap-2 px-4 pt-3 border-t border-line-1"
        style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}
      >
        {isEdit ? (
          <>
            <button
              onClick={onDelete}
              disabled={busy}
              className="btn-secondary flex-1 disabled:opacity-40"
              style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger-dim)' }}
            >
              <Trash2 size={16} />
              Удалить
            </button>
            <button onClick={onSubmit} disabled={!canSubmit} className="btn-primary flex-1">
              Сохранить
            </button>
          </>
        ) : (
          <>
            <button onClick={onClose} disabled={busy} className="btn-secondary flex-1">
              Отмена
            </button>
            <button onClick={onSubmit} disabled={!canSubmit} className="btn-primary flex-1">
              Создать
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ── Просмотр события ──────────────────────────────────────────────────────────

type DayEvent = CalendarDay['events'][number]

const SOURCE_LABEL: Record<string, string> = {
  google: 'Из Google Calendar',
  apple:  'Из Apple Calendar',
}

function EventSheet({
  event, onClose, onEdit, onDelete, busy,
}: {
  event: DayEvent
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
  busy: boolean
}) {
  const timeRange = event.ends_at
    ? `${isoTime(event.starts_at)}–${isoTime(event.ends_at)}`
    : isoTime(event.starts_at)

  return (
    <Sheet onClose={onClose} zIndex={60}>
      <div data-sheet-scroll className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 pb-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <p className="text-h2 text-ink font-display flex-1">{event.title}</p>
          <button onClick={onClose} className="btn-active text-ink-2 p-1 shrink-0" aria-label="Закрыть">
            <X size={20} />
          </button>
        </div>

        <p className="text-base text-ink-1">
          {formatDayTitle(isoDate(event.starts_at))}, {timeRange}
        </p>

        {event.location && (
          <div className="flex items-center gap-2 text-base text-ink-1">
            <MapPin size={15} className="text-ink-2 shrink-0" />
            {event.location}
          </div>
        )}

        {SOURCE_LABEL[event.source] && (
          <p className="text-xs text-ink-2">{SOURCE_LABEL[event.source]}</p>
        )}
      </div>

      <div
        className="shrink-0 flex gap-2 px-4 pt-3 border-t border-line-1"
        style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}
      >
        <button
          onClick={onDelete}
          disabled={busy}
          className="btn-secondary flex-1 disabled:opacity-40"
          style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger-dim)' }}
        >
          <Trash2 size={16} />
          Удалить
        </button>
        <button onClick={onEdit} disabled={busy} className="btn-primary flex-1">
          Изменить
        </button>
      </div>
    </Sheet>
  )
}

// ── Страница ──────────────────────────────────────────────────────────────────

export function Calendar() {
  const qc = useQueryClient()
  const { haptic } = useTelegram()
  const location = useLocation()

  const today = new Date()
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() })
  const [selected, setSelected] = useState<string | null>(null)
  const [viewEvent, setViewEvent] = useState<DayEvent | null>(null)
  const [editor, setEditor] = useState<EditorState | null>(null)

  // Единый стек листов: рендерим только верхний, свайп/крестик возвращают предыдущий.
  const [sheetStack, setSheetStack] = useState<SheetName[]>([])
  const activeSheet = sheetStack[sheetStack.length - 1] ?? null

  const openSheet = (name: SheetName) => setSheetStack(s => [...s, name])
  const replaceSheet = (name: SheetName) => setSheetStack(s => [...s.slice(0, -1), name])
  const closeSheet = () => setSheetStack(s => s.slice(0, -1))

  // Тост об успешной оплате приходит состоянием роутера со страницы подписки
  const [toast, setToast] = useState<string | null>(() => {
    const state = location.state as { toast?: string } | null
    return state?.toast ?? null
  })

  useEffect(() => {
    // Чистим state, чтобы тост не всплыл снова при возврате назад
    if ((location.state as { toast?: string } | null)?.toast) {
      window.history.replaceState({}, '')
    }
  }, [location.state])

  // Диапазон с запасом ±7 дней — чтобы наполнить дни соседних месяцев в сетке
  const rangeFrom = ymd(new Date(cursor.year, cursor.month, -6))
  const rangeTo = ymd(new Date(cursor.year, cursor.month + 1, 7))

  const { data, isLoading, error, refetch } = useQuery<CalendarData>({
    queryKey: ['calendar', cursor.year, cursor.month],
    queryFn: () => calendarApi.getCalendar(rangeFrom, rangeTo),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  })

  const days = data?.days ?? {}

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 4000)
    return () => clearTimeout(t)
  }, [toast])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['calendar'] })
    qc.invalidateQueries({ queryKey: ['tasks'] })
    qc.invalidateQueries({ queryKey: ['reminders'] })
    qc.invalidateQueries({ queryKey: ['today'] })
  }

  // ── Мутации ────────────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: async (s: EditorState) => {
      const title = s.title.trim()
      const at = (time: string) => `${s.date}T${time}:00`

      if (s.kind === 'reminder') {
        if (s.wake) {
          // Напоминание «когда проснусь» — без даты и времени.
          return calendarApi.createReminder({ text: title, trigger_type: 'on_wake' })
        }
        const payload = {
          text: title,
          remind_at: at(s.time),
          recurrence: (s.recurrence || null) as RecurrenceType | null,
        }
        return s.id
          ? calendarApi.updateReminder(s.id, payload)
          : calendarApi.createReminder(payload)
      }

      if (s.kind === 'task') {
        const payload = {
          title,
          due_at: s.hasDue ? at(s.dueTime) : null,
          priority: s.priority,
          tags: s.tags.split(',').map(t => t.trim()).filter(Boolean),
        }
        return s.id
          ? calendarApi.updateTask(s.id, payload)
          : calendarApi.createTask(payload)
      }

      const payload = {
        title,
        starts_at: s.allDay ? `${s.date}T00:00:00` : at(s.time),
        ends_at: s.allDay ? null : at(s.endTime),
        location: s.location.trim() || null,
        description: s.description.trim() || null,
        all_day: s.allDay,
      }
      return s.id
        ? calendarApi.updateEvent(s.id, payload)
        : calendarApi.createEvent(payload)
    },
    onSuccess: (result, s) => {
      invalidate()
      closeSheet()
      setEditor(null)
      haptic.notificationOccurred('success')

      // Статус синхронизации показываем только для событий
      if (s.kind === 'event') {
        const ev = result as EventItem
        const names = (list: string[]) =>
          list.map(n => (n === 'google' ? 'Google Calendar' : 'Apple Calendar')).join(', ')
        if (ev.sync_failed?.length) {
          setToast(`Создано в Delo. Не удалось добавить в ${names(ev.sync_failed)}`)
        } else if (ev.synced?.length) {
          setToast(`Добавлено в ${names(ev.synced)}`)
        }
      }
    },
    onError: (e: Error) => setToast(e.message.replace(/^\d+:\s*/, '') || 'Не удалось сохранить'),
  })

  const deleteMutation = useMutation({
    mutationFn: ({ kind, id }: { kind: ItemKind; id: number }) => {
      if (kind === 'reminder') return calendarApi.deleteReminder(id)
      if (kind === 'task') return calendarApi.deleteTask(id)
      return calendarApi.deleteEvent(id)
    },
    onSuccess: () => {
      invalidate()
      closeSheet()
      setEditor(null)
      setViewEvent(null)
      haptic.notificationOccurred('success')
    },
  })

  // Ручная синхронизация: планировщик тянет внешние календари раз в 30 минут,
  // и ждать столько после правки в Google/Apple неприятно. Кнопка делает то же
  // самое немедленно.
  const syncMutation = useMutation({
    mutationFn: () => calendarApi.syncEvents(),
    onSuccess: (res) => {
      invalidate()
      if (res.calendars.length === 0) {
        setToast('Внешний календарь не подключён — синхронизировать нечего')
      } else if (res.imported === 0 && res.removed === 0) {
        setToast('Уже актуально')
      } else {
        const parts: string[] = []
        if (res.imported) parts.push(`обновлено ${res.imported} ${pluralEvents(res.imported)}`)
        if (res.removed) parts.push(`удалено ${res.removed}`)
        setToast(parts.join(', '))
      }
    },
    onError: (e: Error) =>
      setToast(e.message.replace(/^\d+:\s*/, '') || 'Не удалось синхронизировать'),
  })

  const completeTask = useMutation({
    mutationFn: (id: number) => calendarApi.updateTask(id, { status: 'done' }),
    onSuccess: invalidate,
  })

  // ── Навигация по месяцам ───────────────────────────────────────────────────

  const shiftMonth = (delta: -1 | 1) => {
    haptic.selectionChanged()
    setCursor(c => {
      const d = new Date(c.year, c.month + delta, 1)
      return { year: d.getFullYear(), month: d.getMonth() }
    })
  }

  const goToday = () => {
    haptic.impactOccurred('light')
    const now = new Date()
    setCursor({ year: now.getFullYear(), month: now.getMonth() })
    setSelected(ymd(now))
    openSheet('day')
  }

  const handleSelectDay = (key: string) => {
    haptic.impactOccurred('light')
    setSelected(key)
    openSheet('day')
  }

  const openCreate = (kind: ItemKind = 'reminder', date?: string) => {
    haptic.impactOccurred('light')
    setEditor(newEditor(kind, date ?? selected ?? ymd(new Date())))
    openSheet('form')
  }

  const isCurrentMonth =
    cursor.year === today.getFullYear() && cursor.month === today.getMonth()

  const selectedDay = selected ? (days[selected] ?? emptyDay()) : null
  const busy = saveMutation.isPending || deleteMutation.isPending

  // Сводка дня: сегодня, а если на сегодня пусто — завтра. Данные берём из уже
  // загруженного календаря (актуальны только для текущего месяца сетки).
  const todayKey = ymd(today)
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const tomorrowKey = ymd(tomorrow)
  const todayLines = buildSummary(days[todayKey] ?? emptyDay(), todayKey, true)
  const tomorrowLines = todayLines.length
    ? []
    : buildSummary(days[tomorrowKey] ?? emptyDay(), tomorrowKey, false)
  const showingTomorrow = todayLines.length === 0 && tomorrowLines.length > 0
  const summaryLines = showingTomorrow ? tomorrowLines : todayLines

  // Корень наложения на iOS Safari: backdrop-filter у sticky-шапки заставляет
  // WebKit композитить свой backdrop (верх сетки под шапкой) в отдельный слой,
  // а из-за бага этот слой всплывает поверх fixed-листа из портала — так
  // «фрагмент сетки» и перекрывал форму. Пока открыт любой лист, гасим только
  // backdrop-filter: проблемный слой исчезает, а фон остаётся видимым (шапка и
  // сетка на месте, её собственный фон почти непрозрачный).
  const headerStyle = activeSheet
    ? ({ backdropFilter: 'none', WebkitBackdropFilter: 'none' } as const)
    : undefined

  // Форма открыта — рендерим её как отдельную страницу вместо календаря, а не
  // поверх него. Так композитным слоям сетки нечего перекрывать (её нет в DOM).
  if (activeSheet === 'form' && editor) {
    return (
      <EditorPage
        state={editor}
        busy={busy}
        onChange={setEditor}
        onClose={closeSheet}
        onSubmit={() => saveMutation.mutate(editor)}
        onDelete={() =>
          editor.id !== undefined &&
          deleteMutation.mutate({ kind: editor.kind, id: editor.id })
        }
      />
    )
  }

  return (
    <div className="flex flex-col min-h-full page-enter">
      {/* Заголовок */}
      <div className="sticky top-0 sticky-header z-10 px-4 pt-5 pb-3" style={headerStyle}>
        <div className="flex items-center justify-between">
          <p className="font-display text-ink" style={{ fontSize: 20 }}>
            {MONTHS[cursor.month]} {cursor.year}
          </p>
          <div className="flex gap-1">
            <button
              onClick={() => shiftMonth(-1)}
              className="btn-active flex items-center justify-center rounded-lg bg-surface-2 border border-line-1 text-ink-1"
              style={{ width: 32, height: 32 }}
              aria-label="Предыдущий месяц"
            >
              <ChevronLeft size={17} />
            </button>
            <button
              onClick={() => shiftMonth(1)}
              className="btn-active flex items-center justify-center rounded-lg bg-surface-2 border border-line-1 text-ink-1"
              style={{ width: 32, height: 32 }}
              aria-label="Следующий месяц"
            >
              <ChevronRight size={17} />
            </button>
            <button
              onClick={() => { haptic.impactOccurred('light'); syncMutation.mutate() }}
              disabled={syncMutation.isPending}
              className="btn-active flex items-center justify-center rounded-lg bg-surface-2 border border-line-1 text-ink-1 disabled:opacity-50"
              style={{ width: 32, height: 32 }}
              aria-label="Синхронизировать календари"
            >
              <RefreshCw size={16} className={syncMutation.isPending ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => openCreate()}
              className="btn-active flex items-center justify-center rounded-lg text-white ml-1"
              style={{ width: 32, height: 32, background: 'var(--color-accent-gradient)' }}
              aria-label="Добавить"
            >
              <Plus size={18} />
            </button>
          </div>
        </div>

        {!isCurrentMonth && (
          <button onClick={goToday} className="btn-active text-xs text-accent mt-1.5">
            Сегодня
          </button>
        )}
      </div>

      <div className="px-4 pb-6 space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            <div className="grid grid-cols-7 gap-0.5">
              {Array.from({ length: 42 }).map((_, i) => (
                <Skeleton key={i} className="aspect-square rounded-[10px]" />
              ))}
            </div>
          </div>
        ) : error && !data ? (
          <ErrorCard
            message="Не удалось загрузить календарь"
            onRetry={() => refetch()}
          />
        ) : (
          <MonthGrid
            year={cursor.year}
            month={cursor.month}
            days={days}
            selected={selected}
            onSelect={handleSelectDay}
            onSwipe={shiftMonth}
          />
        )}

        {/* Без даты — задачи без дедлайна + напоминания «когда проснусь» */}
        {((data?.undated_tasks_count ?? 0) + (data?.undated_reminders_count ?? 0)) > 0 && (
          <button
            onClick={() => { haptic.impactOccurred('light'); openSheet('undated') }}
            className="btn-active w-full bg-surface-1 rounded-xl flex items-center justify-between"
            style={{ padding: '12px 16px' }}
          >
            <span className="flex items-center gap-2">
              <span className="text-ink" style={{ fontSize: 14 }}>Без даты</span>
              <span className="text-xs text-accent bg-accent-dim rounded-full px-2 py-0.5">
                {(data!.undated_tasks_count) + (data!.undated_reminders_count)}
              </span>
            </span>
            <ChevronRight size={16} className="text-ink-2" />
          </button>
        )}

        {/* Сводка дня — только для текущего месяца, где today/tomorrow загружены */}
        {isCurrentMonth && data && (
          <TodaySummary
            label={showingTomorrow ? 'Завтра' : 'Сегодня'}
            lines={summaryLines}
            onOpenEvent={ev => { setViewEvent(ev); openSheet('event') }}
            onEditReminder={r => {
              setEditor({
                ...newEditor('reminder', isoDate(r.remind_at)),
                id: r.id,
                title: r.text,
                time: isoTime(r.remind_at),
                recurrence: r.recurrence ?? '',
              })
              openSheet('form')
            }}
            onEditTask={t => {
              setEditor({
                ...newEditor('task', t.due_at ? isoDate(t.due_at) : todayKey),
                id: t.id,
                title: t.title,
                priority: (t.priority as Priority) ?? 'normal',
                hasDue: !!t.due_at,
                dueTime: t.due_at ? isoTime(t.due_at) : '18:00',
              })
              openSheet('form')
            }}
            onAddToday={() => openCreate('reminder', todayKey)}
          />
        )}
      </div>

      {/* Toast — через портал, чтобы оставаться поверх листов (они тоже в body) */}
      {toast && createPortal(
        <div
          className="fixed left-4 right-4 z-[80] toast-enter bg-surface-3 border border-line-2 rounded-xl px-4 py-3 text-sm text-ink shadow-glow"
          style={{ top: 'calc(12px + env(safe-area-inset-top))' }}
          onClick={() => setToast(null)}
        >
          {toast}
        </div>,
        document.body,
      )}

      {/* Список дня */}
      {activeSheet === 'day' && selected && selectedDay && (
        <DaySheet
          dayKey={selected}
          day={selectedDay}
          onClose={closeSheet}
          onAdd={() => openCreate()}
          onCompleteTask={id => { haptic.impactOccurred('light'); completeTask.mutate(id) }}
          onOpenEvent={ev => { setViewEvent(ev); openSheet('event') }}
          onEditReminder={r => {
            setEditor({
              ...newEditor('reminder', isoDate(r.remind_at)),
              id: r.id,
              title: r.text,
              time: isoTime(r.remind_at),
              recurrence: r.recurrence ?? '',
            })
            openSheet('form')
          }}
          onEditTask={t => {
            setEditor({
              ...newEditor('task', t.due_at ? isoDate(t.due_at) : selected),
              id: t.id,
              title: t.title,
              priority: (t.priority as Priority) ?? 'normal',
              hasDue: !!t.due_at,
              dueTime: t.due_at ? isoTime(t.due_at) : '18:00',
            })
            openSheet('form')
          }}
        />
      )}

      {/* Задачи без даты */}
      {activeSheet === 'undated' && (
        <UndatedSheet
          onClose={closeSheet}
          onCompleteTask={id => { haptic.impactOccurred('light'); completeTask.mutate(id) }}
        />
      )}

      {/* Просмотр события */}
      {activeSheet === 'event' && viewEvent && (
        <EventSheet
          event={viewEvent}
          busy={busy}
          onClose={closeSheet}
          onDelete={() => deleteMutation.mutate({ kind: 'event', id: viewEvent.id })}
          onEdit={() => {
            const ev = viewEvent
            setEditor({
              ...newEditor('event', isoDate(ev.starts_at)),
              id: ev.id,
              title: ev.title,
              time: isoTime(ev.starts_at),
              endTime: ev.ends_at ? isoTime(ev.ends_at) : '10:00',
              location: ev.location ?? '',
            })
            replaceSheet('form')
          }}
        />
      )}

      {/* Знакомство при первом запуске */}
      <WelcomeSheet />

      {/* Опрос на 7-й день */}
      <RatingPrompt />
    </div>
  )
}

// ── Список дня ────────────────────────────────────────────────────────────────

interface DaySheetProps {
  dayKey: string
  day: CalendarDay
  onClose: () => void
  onAdd: () => void
  onCompleteTask: (id: number) => void
  onOpenEvent: (e: DayEvent) => void
  onEditReminder: (r: CalendarDay['reminders'][number]) => void
  onEditTask: (t: CalendarDay['tasks'][number]) => void
}

function DaySheet({
  dayKey, day, onClose, onAdd, onCompleteTask, onOpenEvent, onEditReminder, onEditTask,
}: DaySheetProps) {
  const isEmpty = !day.reminders.length && !day.tasks.length && !day.events.length

  return (
    <Sheet onClose={onClose} zIndex={50}>
      <div className="flex items-center justify-between px-4 pb-2">
        <p className="text-h3 text-ink">{formatDayTitle(dayKey)}</p>
        <button onClick={onClose} className="btn-active text-ink-2 p-1" aria-label="Закрыть">
          <X size={20} />
        </button>
      </div>

      <div
        data-sheet-scroll
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 space-y-2"
        style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}
      >
        {isEmpty && (
          <div className="py-8 flex flex-col items-center gap-3">
            <p className="text-base text-ink-1">Ничего не запланировано</p>
            <button onClick={onAdd} className="btn-primary" style={{ height: 40 }}>
              Добавить
            </button>
          </div>
        )}

        {day.reminders.map(r => (
          <button
            key={`r-${r.id}-${r.remind_at}`}
            onClick={() => onEditReminder(r)}
            className="btn-active w-full bg-surface-2 border border-line-1 rounded-xl p-3 flex items-center gap-3 text-left"
          >
            <Bell size={16} style={{ color: DOT_COLORS.reminder }} className="shrink-0" />
            <span className="text-sm text-ink-1 tabular-nums shrink-0">{isoTime(r.remind_at)}</span>
            <span className="text-base text-ink flex-1 min-w-0 truncate">{r.text}</span>
          </button>
        ))}

        {day.tasks.map(t => (
          <div
            key={`t-${t.id}`}
            className="bg-surface-2 border border-line-1 rounded-xl p-3 flex items-center gap-3"
          >
            <CheckSquare size={16} style={{ color: DOT_COLORS.task }} className="shrink-0" />
            <button onClick={() => onEditTask(t)} className="flex-1 min-w-0 text-left">
              <span className="text-base text-ink block truncate">{t.title}</span>
              {t.due_at && (
                <span className="text-xs text-ink-2 tabular-nums">{isoTime(t.due_at)}</span>
              )}
            </button>
            <button
              onClick={() => onCompleteTask(t.id)}
              className="btn-active w-6 h-6 rounded-full border-2 border-accent shrink-0 hover:bg-accent-dim transition-colors"
              aria-label="Выполнено"
            />
          </div>
        ))}

        {day.events.map(e => (
          <button
            key={`e-${e.id}`}
            onClick={() => onOpenEvent(e)}
            className="btn-active w-full bg-surface-2 border border-line-1 rounded-xl p-3 flex items-start gap-3 text-left"
          >
            <CalendarIcon size={16} style={{ color: DOT_COLORS.event }} className="shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <span className="text-sm text-ink-1 tabular-nums">
                {isoTime(e.starts_at)}{e.ends_at ? `–${isoTime(e.ends_at)}` : ''}
              </span>
              <span className="text-base text-ink block truncate">{e.title}</span>
              {e.location && (
                <span className="text-xs text-ink-2 block truncate">{e.location}</span>
              )}
            </div>
          </button>
        ))}
      </div>
    </Sheet>
  )
}

// ── Задачи без даты ───────────────────────────────────────────────────────────

function UndatedSheet({
  onClose, onCompleteTask,
}: {
  onClose: () => void
  onCompleteTask: (id: number) => void
}) {
  const { data, isLoading } = useQuery<TaskItem[]>({
    queryKey: ['tasks', 'pending'],
    queryFn: () => api.get('/api/tasks?status=pending'),
    staleTime: 30_000,
  })

  const { data: reminders } = useQuery<ReminderItem[]>({
    queryKey: ['reminders'],
    queryFn: () => api.get('/api/reminders'),
    staleTime: 30_000,
  })

  const undated = (data ?? []).filter(t => !t.due_at)
  const wakeReminders = (reminders ?? []).filter(r => r.trigger_type === 'on_wake')
  const isEmpty = !isLoading && undated.length === 0 && wakeReminders.length === 0

  return (
    <Sheet onClose={onClose} zIndex={50}>
      <div className="flex items-center justify-between px-4 pb-2">
        <p className="text-h3 text-ink">Без даты</p>
        <button onClick={onClose} className="btn-active text-ink-2 p-1" aria-label="Закрыть">
          <X size={20} />
        </button>
      </div>

      <div
        data-sheet-scroll
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 space-y-2"
        style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}
      >
        {isLoading && <Skeleton className="h-14 w-full" />}
        {isEmpty && (
          <p className="text-base text-ink-1 text-center py-8">Ничего без даты нет</p>
        )}
        {wakeReminders.map(r => (
          <div
            key={`wr-${r.id}`}
            className="bg-surface-2 border border-line-1 rounded-xl p-3 flex items-center gap-3"
          >
            <Sunrise size={16} style={{ color: DOT_COLORS.reminder }} className="shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-base text-ink block truncate">{r.text}</span>
              <span className="text-xs text-ink-2">когда проснёшься</span>
            </div>
          </div>
        ))}
        {undated.map(t => (
          <div
            key={t.id}
            className="bg-surface-2 border border-line-1 rounded-xl p-3 flex items-center gap-3"
          >
            <CheckSquare size={16} style={{ color: DOT_COLORS.task }} className="shrink-0" />
            <span className="text-base text-ink flex-1 min-w-0 truncate">{t.title}</span>
            <button
              onClick={() => onCompleteTask(t.id)}
              className="btn-active w-6 h-6 rounded-full border-2 border-accent shrink-0 hover:bg-accent-dim transition-colors"
              aria-label="Выполнено"
            />
          </div>
        ))}
      </div>
    </Sheet>
  )
}
